import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useTheme } from '../../hooks/useTheme.jsx';
import { User, Mail, Shield, Building, Save, Camera, Download, Phone, Linkedin, Briefcase, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { formatDate } from '../../utils/helpers';
import ShareTabNote from '../common/ShareTabNote.jsx';
import BankDocumentReader from '../contracts/BankDocumentReader.jsx';

// Country-aware optional "local bank code" (stored in the iban field). Mirrors the backend
// (services/banking_validation.py) so the label + format check match on both sides.
const IBAN_COUNTRIES = new Set(['germany','france','spain','italy','netherlands','ireland','belgium','portugal','austria','switzerland','sweden','norway','denmark','finland','poland','greece','united arab emirates','uae','saudi arabia','qatar','kuwait','bahrain','oman','pakistan','turkey','luxembourg','romania','hungary','czech republic']);
function localCodeMeta(country) {
    const c = (country || '').trim().toLowerCase();
    if (['india','in'].includes(c)) return { label: 'IFSC Code', re: /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/ };
    if (['united states','usa','us','u.s.','united states of america'].includes(c)) return { label: 'Routing / ABA number', re: /^\d{9}$/ };
    if (['united kingdom','uk','england','great britain','u.k.'].includes(c)) return { label: 'IBAN or Sort code', re: null };
    if (IBAN_COUNTRIES.has(c)) return { label: 'IBAN', re: /^[A-Za-z]{2}\d{2}[A-Za-z0-9]{10,30}$/ };
    return { label: 'Local bank / routing code', re: null };
}
const SWIFT_RE = /^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/;
const BANKING_REQUIRED = [
    ['account_holder_name', 'Account holder name'], ['mailing_address', 'Postal mailing address'],
    ['bank_name', 'Bank name'], ['bank_address', 'Bank address'], ['account_number', 'Account number'],
    ['swift_code', 'SWIFT / BIC code'], ['currency', 'Currency of account'], ['country', 'Country of account operation'],
];
function bankingFormatIssues(b) {
    const issues = [];
    for (const [key, label] of BANKING_REQUIRED) if (!(b[key] || '').trim()) issues.push(`${label} is required`);
    if ((b.mailing_address || '').trim() && /\S+@\S+\.\S+/.test(b.mailing_address)) issues.push('Postal mailing address looks like an email — enter a physical address');
    const sw = (b.swift_code || '').replace(/\s/g, '');
    if (sw && !SWIFT_RE.test(sw)) issues.push('SWIFT / BIC code format looks invalid (expected 8 or 11 letters/digits)');
    const lc = (b.iban || '').replace(/\s/g, '');
    if (lc) { const { label, re } = localCodeMeta(b.country); if (re && !re.test(lc)) issues.push(`${label} format looks invalid`); }
    const acct = (b.account_number || '').replace(/[\s-]/g, '');
    if (acct && !(acct.length >= 5 && acct.length <= 34 && /^[A-Za-z0-9]+$/.test(acct))) issues.push('Account number looks invalid (5–34 letters/digits)');
    return issues;
}

const ProfileSettings = () => {
    const { user } = useAuth();
    // `t` is this screen's own strings; `tc` reaches the shared catalogue for words like
    // Cancel and Save that appear on nearly every screen and should read identically on all
    // of them.
    const { t } = useTranslation('profile');
    const { t: tc } = useTranslation('common');
    const { isDark } = useTheme();
    // When arriving from a "Update Banking Details" button (contract or invoice), we land
    // with ?edit=banking&return=<url>: auto-open edit mode, scroll to banking, and after a
    // successful save send the consultant back where they came from.
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const returnTo = searchParams.get('return');
    const bankingRef = useRef(null);

    const [formData, setFormData] = useState({
        full_name: '',
        department: '',
        phone_number: '',
        linkedin_url: '',
        specialization: '',
        about: '',
        company_name: '',
        organization_name: '',
        availability: '',
    });

    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState(null);
    // Change-email flow (email is the login identity → requires password + token reissue).
    // NEITHER of the two blocks below is a <form>, and that is not a style choice.
    //
    // The whole page is already one <form> (see handleSave). A <form> inside a <form> is invalid
    // HTML: the browser silently drops the inner one, so onSubmit never fires and a
    // type="submit" button submits the OUTER form instead. The password form shipped that way
    // for one deploy — pressing "Update Password" saved the profile and left the password
    // untouched, while the panel closed as though it had worked. The email form had the same
    // defect from the day it was written.
    //
    // So: plain divs, and type="button" with an explicit onClick.
    const [showEmailForm, setShowEmailForm] = useState(false);
    // Change-password flow. Deliberately mirrors the email change beside it: the CURRENT
    // password is what proves ownership, because being signed in only proves somebody has the
    // screen — a laptop left unlocked is not consent.
    const [showPwForm, setShowPwForm] = useState(false);
    // Which boxes are currently readable. Per box, not one switch for all of them: somebody
    // checking they typed the NEW password twice does not want their current one on screen too.
    const [pwShown, setPwShown] = useState({});
    const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [pwBusy, setPwBusy] = useState(false);
    const [pwErr, setPwErr] = useState(null);
    const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
    const [emailBusy, setEmailBusy] = useState(false);
    const [emailErr, setEmailErr] = useState(null);
    // A password box with a show/hide eye. Typing a password you cannot see, twice, with no way
    // to check, is how people end up locked out of an account they just changed.
    const secretBox = (name, placeholder, value, onChange) => (
        <div className="relative">
            <input
                type={pwShown[name] ? 'text' : 'password'}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                className="w-full border border-gray-300 rounded-lg pl-3 pr-10 py-2 text-sm"
            />
            <button
                type="button"
                onClick={() => setPwShown(v => ({ ...v, [name]: !v[name] }))}
                aria-label={pwShown[name] ? 'Hide password' : 'Show password'}
                title={pwShown[name] ? 'Hide' : 'Show'}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
            >
                {pwShown[name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
        </div>
    );

    const submitPasswordChange = async (e) => {
        e?.preventDefault?.();
        setPwErr(null);
        if (!pwForm.current_password) { setPwErr('Enter your current password.'); return; }
        if (pwForm.new_password.length < 8) { setPwErr('The new password must be at least 8 characters.'); return; }
        if (pwForm.new_password !== pwForm.confirm) { setPwErr('The two new passwords do not match.'); return; }
        setPwBusy(true);
        try {
            await api.put('/users/me/password', {
                current_password: pwForm.current_password,
                new_password: pwForm.new_password,
            });
            setPwForm({ current_password: '', new_password: '', confirm: '' });
            setShowPwForm(false);
            alert('Password updated.');
        } catch (err) {
            setPwErr(err.response?.data?.detail || 'Could not update the password.');
        } finally { setPwBusy(false); }
    };

    // NOT a form submit handler. The whole page is already one <form> (see handleSave), and a
    // <form> inside a <form> is invalid HTML — the browser drops the inner one, so onSubmit never
    // fires and the button silently re-saves the profile instead. Plain buttons calling these
    // directly, with the validation done here rather than by required/minLength attributes that
    // only work inside a real form.
    const submitEmailChange = async () => {
        if (!emailForm.new_email.trim() || !emailForm.password) {
            setEmailErr('Enter the new email and your current password.');
            return;
        }
        setEmailBusy(true); setEmailErr(null);
        try {
            const r = await api.put('/users/me/email', emailForm);
            // Persist the new email + reissued token so the session keeps working.
            try {
                const stored = JSON.parse(localStorage.getItem('user')) || {};
                stored.email = r.data.email;
                if (r.data.access_token) stored.access_token = r.data.access_token;
                localStorage.setItem('user', JSON.stringify(stored));
            } catch { /* ignore */ }
            setFeedback({ type: 'success', text: 'Email updated.' });
            setShowEmailForm(false);
            setEmailForm({ new_email: '', password: '' });
            setTimeout(() => window.location.reload(), 700);
        } catch (err) {
            setEmailErr(err.response?.data?.detail || 'Could not change email.');
        } finally { setEmailBusy(false); }
    };
    const [resumeUploading, setResumeUploading] = useState(false);
    const [hasCv, setHasCv] = useState(false);
    const emptyBanking = { account_holder_name: '', mailing_address: '', bank_name: '', bank_address: '', account_number: '', iban: '', swift_code: '', currency: 'USD', country: '' };
    const [banking, setBanking] = useState(emptyBanking);
    const [bankingConfirmedAt, setBankingConfirmedAt] = useState(null);
    const [savedBankingIssues, setSavedBankingIssues] = useState([]);   // from GET — flags details saved before validation

    // Sync formData when the user from context loads/changes
    useEffect(() => {
        if (user) {
            setFormData({
                full_name: user.full_name || '',
                department: user.department || '',
                phone_number: user.phone_number || '',
                linkedin_url: user.linkedin_url || '',
                specialization: user.specialization || '',
                about: user.about || '',
                company_name: user.company_name || '',
                organization_name: user.organization_name || '',
                availability: user.availability || '',
            });
            setHasCv(!!user.has_cv || !!user.resume_url);
            // Consultants: load their own banking details so they can view/update here.
            if (user.role === 'Consultant') {
                api.get('/users/me/banking').then(r => {
                    if (r.data?.has_banking) {
                        setBanking({
                            account_holder_name: r.data.account_holder_name || '',
                            mailing_address: r.data.mailing_address || '',
                            bank_name: r.data.bank_name || '',
                            bank_address: r.data.bank_address || '',
                            account_number: r.data.account_number || '',
                            iban: r.data.iban || '',
                            swift_code: r.data.swift_code || '',
                            currency: r.data.currency || 'USD',
                            country: r.data.country || '',
                        });
                        setBankingConfirmedAt(r.data.last_confirmed_at || null);
                    }
                    setSavedBankingIssues(r.data?.issues || []);
                }).catch(() => {});
            }
        }
    }, [user]);

    // Arrived via a banking-update link → open edit mode and scroll the banking block
    // into view so the consultant lands right on the fields.
    useEffect(() => {
        if (searchParams.get('edit') === 'banking' && user?.role === 'Consultant') {
            setIsEditing(true);
            setTimeout(() => bankingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
        }
    }, [searchParams, user]);

    const handleResumeUpload = async (file) => {
        if (!file) return;
        setResumeUploading(true);
        setFeedback(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post('/users/me/resume', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setHasCv(true);
            setFeedback({ type: 'success', text: 'Résumé uploaded.' });
        } catch (err) {
            setFeedback({ type: 'error', text: err.response?.data?.detail || 'Résumé upload failed.' });
        } finally {
            setResumeUploading(false);
        }
    };

    const viewResume = async () => {
        try {
            const r = await api.get(`/users/${user.id}/cv`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            setFeedback({ type: 'error', text: err.response?.data?.detail || t('resume_open_failed') });
        }
    };

    // All 8 fields the consultancy agreement's Annexure B requires — once the consultant has
    // started entering banking (any field non-empty), every field must be filled before saving.
    // Someone who hasn't touched banking yet can still save the rest of their profile freely.
    // IBAN is OPTIONAL (many accounts use only an account number / SWIFT) — not in the required set.
    const BANKING_FIELDS = ['account_holder_name', 'mailing_address', 'bank_name', 'bank_address', 'account_number', 'swift_code', 'currency', 'country'];
    const bankingStarted = BANKING_FIELDS.some(k => (banking[k] || '').trim());
    const bankingIncomplete = bankingStarted && BANKING_FIELDS.filter(k => !(banking[k] || '').trim());
    // Full validation (required + SWIFT/local-code/account formats) — blocks save.
    const bankingErrors = bankingStarted ? bankingFormatIssues(banking) : [];

    const handleSave = async (e) => {
        e.preventDefault();
        if (user?.role === 'Consultant' && bankingStarted && bankingErrors.length > 0) {
            setFeedback({ type: 'error', text: `Please fix your bank details before saving: ${bankingErrors.join('; ')}.` });
            setTimeout(() => bankingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
            return;
        }
        setSaving(true);
        setFeedback(null);
        try {
            const resp = await api.put('/users/me/', formData);

            // Consultants: also save banking details (only once ALL 8 required fields are filled
            // — see bankingIncomplete gate above; this condition just decides whether to call the
            // banking endpoint at all for someone who hasn't started on it yet).
            if (user?.role === 'Consultant' && bankingStarted) {
                await api.put('/users/me/banking', banking);
                setBankingConfirmedAt(new Date().toISOString());
            }

            // Update the stored localStorage user so the UI stays in sync after save
            const stored = JSON.parse(localStorage.getItem('user') || '{}');
            const merged = { ...stored, ...resp.data };
            localStorage.setItem('user', JSON.stringify(merged));

            setIsEditing(false);
            // Came here from a contract/invoice "Update Banking" button → go back there.
            if (returnTo) {
                setFeedback({ type: 'success', text: 'Banking details saved. Taking you back…' });
                setTimeout(() => navigate(returnTo), 900);
                return;
            }
            setFeedback({ type: 'success', text: 'Profile updated successfully. Refresh to see changes everywhere.' });
        } catch (err) {
            setFeedback({ type: 'error', text: err.response?.data?.detail || 'Failed to update profile' });
        } finally {
            setSaving(false);
        }
    };

    const handleExport = () => {
        // Phase A2: Export profile as JSON download (mentioned by user as "export button")
        if (!user) return;
        const exportable = {
            full_name: user.full_name,
            email: user.email,
            role: user.role,
            department: user.department,
            phone_number: user.phone_number,
            linkedin_url: user.linkedin_url,
            specialization: user.specialization,
            company_name: user.company_name,
            organization_name: user.organization_name,
            availability: user.availability,
            years_of_experience: user.years_of_experience,
            certifications: user.certifications,
            hourly_rate: user.hourly_rate,
            contract_type: user.contract_type,
            created_at: user.created_at,
            exported_at: new Date().toISOString(),
        };
        const blob = new Blob([JSON.stringify(exportable, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(user.full_name || 'profile').replace(/[^a-z0-9]/gi, '_')}_profile.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setFeedback({ type: 'success', text: 'Profile exported.' });
    };

    // Dynamic Theme Classes
    const cardClass = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
    const inputClass = `w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${isDark
        ? 'bg-gray-900 border-gray-700 text-white focus:border-blue-500'
        : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
        }`;

    const firstChar = (user?.full_name || user?.email || 'U').charAt(0).toUpperCase();

    return (
        <div className={`min-h-full p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="max-w-4xl mx-auto space-y-6">

                {/* Feedback toast */}
                {feedback && (
                    <div
                        className={`px-4 py-3 rounded-lg text-sm border ${feedback.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}
                        onClick={() => setFeedback(null)}
                    >
                        {feedback.text}
                    </div>
                )}

                {/* header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div>
                        <h1 className={`text-2xl font-bold ${textPrimary}`}>{t('title')}</h1>
                        <p className={`mt-1 text-sm ${textSecondary}`}>{t('subtitle')}</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleExport}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Export
                        </button>
                        <button
                            onClick={() => setIsEditing(!isEditing)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${isEditing
                                ? 'bg-gray-500 hover:bg-gray-600 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30'
                                }`}
                        >
                            {isEditing ? tc('actions.cancel') : t('edit')}
                        </button>
                    </div>
                </div>

                {/* Profile Card */}
                <form onSubmit={handleSave} className={`p-8 rounded-2xl border shadow-sm ${cardClass}`}>
                    <ShareTabNote className="mb-6" />

                    <div className="flex flex-col md:flex-row gap-8 items-start">

                        {/* Avatar Section */}
                        <div className="flex flex-col items-center space-y-4">
                            <div className="relative group">
                                <div className="w-32 h-32 rounded-full bg-linear-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-4xl font-bold text-white shadow-xl ring-4 ring-opacity-50 ring-blue-500">
                                    {firstChar}
                                </div>
                            </div>
                            <div className="text-center">
                                <h3 className={`text-xl font-bold ${textPrimary}`}>{user?.full_name || 'User'}</h3>
                                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full mt-2 inline-block">
                                    {user?.display_role || user?.role}
                                </span>
                            </div>
                        </div>

                        {/* Form Section */}
                        <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="col-span-1">
                                <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('full_name')}</label>
                                <div className="relative">
                                    <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                    <input
                                        type="text"
                                        value={formData.full_name}
                                        disabled={!isEditing}
                                        className={`${inputClass} pl-10`}
                                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="col-span-1">
                                <label className={`block text-sm font-medium mb-2 ${textSecondary} flex items-center justify-between`}>
                                    <span>{t('email')}</span>
                                    <button type="button" onClick={() => { setShowEmailForm(v => !v); setEmailErr(null); }}
                                        className="text-xs text-blue-600 hover:underline font-normal">
                                        {showEmailForm ? 'Cancel' : 'Change'}
                                    </button>
                                </label>
                                <div className="relative">
                                    <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                    <input
                                        type="email"
                                        value={user?.email || ''}
                                        className={`${inputClass} pl-10 opacity-70 cursor-not-allowed`}
                                        readOnly
                                    />
                                </div>
                                {showEmailForm && (
                                    <div className="mt-2 border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50/60">
                                        <input type="email" placeholder={t('ph_new_email')} value={emailForm.new_email}
                                            onChange={e => setEmailForm(f => ({ ...f, new_email: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                        <input type="password" placeholder={t('ph_current_pw_confirm')} value={emailForm.password}
                                            onChange={e => setEmailForm(f => ({ ...f, password: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                        {emailErr && <p className="text-xs text-red-500">{emailErr}</p>}
                                        <p className="text-[10px] text-gray-400">Your email is your login. Changing it keeps you signed in (a new session token is issued automatically).</p>
                                        <button type="button" onClick={submitEmailChange} disabled={emailBusy}
                                            className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                            {emailBusy ? 'Updating…' : 'Update Email'}
                                        </button>
                                    </div>
                                )}

                                {/* Password sits with the email because they are the same thing to
                                    the person using it: how I get in. Until this existed there was
                                    no way to change one at all on production. */}
                                <div className="mt-3">
                                    <button type="button" onClick={() => { setShowPwForm(v => !v); setPwErr(null); }}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-700">
                                        {showPwForm ? 'Cancel' : 'Change password'}
                                    </button>
                                </div>
                                {showPwForm && (
                                    <div className="mt-2 border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50/60">
                                        {secretBox('current', 'Current password', pwForm.current_password,
                                            e => setPwForm(f => ({ ...f, current_password: e.target.value })))}
                                        {secretBox('new', 'New password (at least 8 characters)', pwForm.new_password,
                                            e => setPwForm(f => ({ ...f, new_password: e.target.value })))}
                                        {secretBox('repeat', 'Repeat the new password', pwForm.confirm,
                                            e => setPwForm(f => ({ ...f, confirm: e.target.value })))}
                                        {pwErr && <p className="text-xs text-red-500">{pwErr}</p>}
                                        <p className="text-[10px] text-gray-400">You stay signed in — your session is tied to your email, not your password.</p>
                                        <button type="button" onClick={submitPasswordChange} disabled={pwBusy}
                                            className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                            {pwBusy ? 'Updating…' : 'Update Password'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="col-span-1">
                                <label className={`block text-sm font-medium mb-2 ${textSecondary} flex items-center justify-between`}>
                                    <span>{t('password')}</span>
                                    <button type="button" onClick={() => { setShowPwForm(v => !v); setPwErr(null); }}
                                        className="text-xs text-blue-600 hover:underline font-normal">
                                        {showPwForm ? 'Cancel' : 'Change'}
                                    </button>
                                </label>
                                <div className="relative">
                                    <Shield className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                    <input type="password" value="••••••••" readOnly
                                        className={`${inputClass} pl-10 opacity-70 cursor-not-allowed`} />
                                </div>
                                {showPwForm && (
                                    <div className="mt-2 border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50/60">
                                        <input type="password" autoComplete="current-password"
                                            placeholder={t('ph_current_pw')} value={pwForm.current_password}
                                            onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                        <input type="password" autoComplete="new-password"
                                            placeholder={t('ph_new_pw')} value={pwForm.new_password}
                                            onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                        <input type="password" autoComplete="new-password"
                                            placeholder={t('ph_repeat_pw')} value={pwForm.confirm}
                                            onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                                        {pwErr && <p className="text-xs text-red-500">{pwErr}</p>}
                                        <p className="text-[10px] text-gray-400">You stay signed in here. Use the new password next time you sign in.</p>
                                        <button type="button" onClick={submitPasswordChange} disabled={pwBusy}
                                            className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                            {pwBusy ? 'Updating…' : 'Update Password'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="col-span-1">
                                <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('role_ro')}</label>
                                <div className="relative">
                                    <Shield className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                    <input
                                        type="text"
                                        value={user?.role || ''}
                                        disabled
                                        className={`${inputClass} pl-10 opacity-70 cursor-not-allowed`}
                                    />
                                </div>
                            </div>

                            <div className="col-span-1">
                                <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('department')}</label>
                                <div className="relative">
                                    <Building className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                    <input
                                        type="text"
                                        value={formData.department}
                                        disabled={!isEditing}
                                        className={`${inputClass} pl-10`}
                                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="col-span-1">
                                <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('phone')}</label>
                                <div className="relative">
                                    <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                    <input
                                        type="tel"
                                        data-private
                                        value={formData.phone_number}
                                        disabled={!isEditing}
                                        className={`${inputClass} pl-10`}
                                        onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="col-span-1">
                                <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('linkedin')}</label>
                                <div className="relative">
                                    <Linkedin className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                    <input
                                        type="url"
                                        value={formData.linkedin_url}
                                        disabled={!isEditing}
                                        className={`${inputClass} pl-10`}
                                        onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* Role-specific extras */}
                            {(user?.role === 'Consultant') && (
                                <div className="col-span-1 md:col-span-2">
                                    <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('specialization')}</label>
                                    <div className="relative">
                                        <Briefcase className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                                        <input
                                            type="text"
                                            value={formData.specialization}
                                            disabled={!isEditing}
                                            className={`${inputClass} pl-10`}
                                            onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}

                            {(user?.role === 'Consultant') && (
                                <div className="col-span-1 md:col-span-2">
                                    <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('about')}</label>
                                    <textarea
                                        rows={4}
                                        value={formData.about}
                                        disabled={!isEditing}
                                        placeholder="A short summary of your experience, skills and focus areas. This helps Project Managers find you by keyword."
                                        className={inputClass}
                                        onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                                    />
                                    <p className={`text-xs mt-1 ${textSecondary}`}>Visible to Project Managers and Admins, and searchable when they look for consultants.</p>
                                </div>
                            )}

                            {(user?.role === 'Consultant') && (
                                <div className="col-span-1 md:col-span-2 border-t border-gray-200 pt-4">
                                    <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>Résumé / CV</label>
                                    <div className="flex flex-wrap items-center gap-3">
                                        {hasCv ? (
                                            <button type="button" onClick={viewResume} className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-700 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100">
                                                <Download className="w-4 h-4" /> View current résumé
                                            </button>
                                        ) : (
                                            <span className={`text-sm ${textSecondary}`}>{t('no_resume')}</span>
                                        )}
                                        <label className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                                            {resumeUploading ? 'Uploading…' : (hasCv ? 'Replace résumé' : 'Upload résumé')}
                                            <input type="file" accept=".pdf,.doc,.docx" className="hidden"
                                                onChange={(e) => { handleResumeUpload(e.target.files?.[0]); e.target.value = '' }} />
                                        </label>
                                    </div>
                                    <p className={`text-xs mt-1 ${textSecondary}`}>{t('resume_hint')}</p>
                                </div>
                            )}

                            {(user?.role === 'Consultant') && (
                                <div ref={bankingRef} className="col-span-1 md:col-span-2 border-t border-gray-200 pt-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className={`block text-sm font-medium ${textSecondary}`}>{t('bank_details')} <span className="text-red-500">*</span></label>
                                        {bankingConfirmedAt && <span className="text-[11px] text-gray-400">Last updated {formatDate(bankingConfirmedAt)}</span>}
                                    </div>
                                    <p className={`text-xs mb-3 ${textSecondary}`}>
                                        Required — these are the exact details that go on your consultancy agreement and every invoice.
                                        Payments are sent to this account; you'll be asked to confirm it before approving any invoice.
                                    </p>
                                    {/* Alert for details saved before validation existed (also raised in the bell). */}
                                    {!isEditing && savedBankingIssues.length > 0 && (
                                        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                            ⚠ Your saved bank details need updating before your next payment:
                                            <ul className="list-disc pl-5 mt-1">{savedBankingIssues.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                            <button type="button" onClick={() => setIsEditing(true)} className="mt-1 font-medium text-amber-900 underline">{t('update_now')}</button>
                                        </div>
                                    )}
                                    {/* Same reader as the contract banking form — a consultant fixing details here
                                        is looking at the same cheque they would be looking at there. */}
                                    {isEditing && (
                                        <div className="mb-3">
                                            <BankDocumentReader onApply={read => setBanking(b => ({ ...b, ...read }))} />
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {[
                                            { key: 'account_holder_name', placeholder: 'Account holder name' },
                                            { key: 'mailing_address', placeholder: 'Postal mailing address' },
                                            { key: 'bank_name', placeholder: 'Bank name' },
                                            { key: 'bank_address', placeholder: 'Bank address' },
                                            { key: 'account_number', placeholder: 'Account number' },
                                            { key: 'iban', placeholder: localCodeMeta(banking.country).label, optional: true },
                                            { key: 'swift_code', placeholder: 'SWIFT / BIC code' },
                                            { key: 'currency', placeholder: 'Currency of account (e.g. USD)' },
                                            { key: 'country', placeholder: 'Country of account operation' },
                                        ].map(({ key, placeholder, optional }) => {
                                            const missing = isEditing && bankingIncomplete && bankingIncomplete.includes(key);
                                            const looksLikeEmail = key === 'mailing_address' && /\S+@\S+\.\S+/.test(banking[key] || '');
                                            return (
                                                <div key={key}>
                                                    <input
                                                        type="text"
                                                        data-private
                                                        placeholder={optional ? `${placeholder} (optional)` : `${placeholder} *`}
                                                        value={banking[key]}
                                                        disabled={!isEditing}
                                                        className={`${inputClass} ${missing ? 'border-red-400! focus:border-red-500!' : ''}`}
                                                        onChange={(e) => setBanking({ ...banking, [key]: e.target.value })}
                                                    />
                                                    {looksLikeEmail && (
                                                        <p className="text-[11px] text-amber-600 mt-1">That looks like an email address. Please enter your postal/home mailing address here.</p>
                                                    )}
                                                    {key === 'iban' && (
                                                        <p className="text-[11px] text-gray-500 mt-1">Optional — add your {localCodeMeta(banking.country).label} if you have it; it helps your payments process faster, without delays.</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {isEditing && bankingStarted && bankingErrors.length > 0 && (
                                        <ul className="text-xs text-red-500 mt-2 list-disc pl-5">
                                            {bankingErrors.map((e, i) => <li key={i}>{e}</li>)}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {(user?.role === 'Funder') && (
                                <div className="col-span-1 md:col-span-2">
                                    <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('organization')}</label>
                                    <input
                                        type="text"
                                        value={formData.organization_name}
                                        disabled={!isEditing}
                                        className={inputClass}
                                        onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                                    />
                                </div>
                            )}

                            {isEditing && (
                                <div className="col-span-1 md:col-span-2 flex justify-end pt-4">
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg shadow-lg shadow-green-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Save className="w-4 h-4" />
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProfileSettings;
